import { useAuth } from '@/features/auth/useAuth';
import { cn } from '@/lib/cn';
import { type NotificationLabelColor, isActionableNotification } from '@coreltb/shared/constants';
import type { Notification } from '@coreltb/shared/types';
import { Bell, Check, CheckCheck, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationBatch,
  useNotificationLabels,
  useNotifications,
  useResolveNotification,
} from './api';
import { type NotificationGroup, groupNotifications, seenPlan } from './groupNotifications';
import { LABEL_DOT } from './labelColors';
import { notificationLabel } from './labels';
import { formatRelative } from './time';

/**
 * Bell w topbar — pokazuje liczbe nieprzeczytanych powiadomien.
 * Klik → popover z lista (tylko nieprzeczytane). Item ma button "oznacz przeczytane".
 * Pod popover'em link "Zobacz wszystkie" → /notyfikacje (pelna historia).
 *
 * Polling 10s przez useNotifications (TanStack Query).
 */
export function AttentionBell() {
  // 'todo' filter — bell pokazuje unresolved (NEW + TODO), wszystko co
  // wymaga uwagi. Backend 'unread' = legacy alias dla 'todo'.
  const { data } = useNotifications('todo', 20);
  const items = data?.items ?? [];
  const unreadCount = data?.unreadCount ?? 0;
  const markRead = useMarkNotificationRead();
  const resolveOne = useResolveNotification();
  const markAllRead = useMarkAllNotificationsRead();
  const batch = useNotificationBatch();
  const { user } = useAuth();
  const role = user?.role ?? null;
  const groups = useMemo(() => groupNotifications(items, role), [items, role]);
  const { data: labels } = useNotificationLabels();

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click outside → close
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="relative inline-flex min-h-touch min-w-touch items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg active:bg-surface-3 focus-visible:bg-surface-2 md:min-h-0 md:min-w-0 md:p-2"
        aria-label={`Powiadomienia (${unreadCount})`}
      >
        <Bell className="h-[18px] w-[18px]" strokeWidth={2} />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-primary-fg">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-3 top-[4.25rem] z-40 overflow-hidden rounded-lg border border-border bg-surface shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-fg">Powiadomienia</h3>
            <div className="flex items-center gap-1.5">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead.mutate({ resolved: true })}
                  disabled={markAllRead.isPending}
                  className="rounded px-2 py-1 text-xs text-fg-muted hover:bg-surface-2 hover:text-fg disabled:opacity-50"
                  title="Oznacz wszystkie powiadomienia jako załatwione (zniknie z dzwoneczka)"
                >
                  Oznacz wszystkie
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded p-1 text-fg-muted hover:bg-surface-2 hover:text-fg"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-8 text-center text-sm text-fg-muted">
                Wszystko przeczytane.
                <div className="mt-1 text-xs text-fg-subtle">
                  Powiadomienia pojawią się tu, gdy klient obejrzy ofertę
                  <br />
                  albo nadejdzie czas przypomnienia.
                </div>
              </div>
            ) : (
              <ul>
                {groups.map((g) => {
                  const single = g.items.length === 1 ? g.items[0] : undefined;
                  if (!single) {
                    return (
                      <ClientGroupItem
                        key={g.key}
                        group={g}
                        labelDot={g.leadId ? labels?.leads[g.leadId] : undefined}
                        // Klik w klienta niczego nie zamyka — odhacza handlowiec (Wiktor).
                        onOpen={() => {
                          const plan = seenPlan(g.items);
                          if (plan.read.length > 0) batch.mutate(plan);
                        }}
                        onResolveAll={() => batch.mutate({ resolve: g.unresolvedIds })}
                        onClose={() => setOpen(false)}
                      />
                    );
                  }
                  return (
                    <NotificationItem
                      key={g.key}
                      notification={single}
                      isActionable={isActionableNotification(single.type, role)}
                      onMarkRead={() => markRead.mutate(single.id)}
                      onResolve={() => resolveOne.mutate(single.id)}
                      onClose={() => setOpen(false)}
                    />
                  );
                })}
              </ul>
            )}
          </div>

          <Link
            to="/notyfikacje"
            onClick={() => setOpen(false)}
            className="block border-t border-border bg-bg/40 px-4 py-2.5 text-center text-xs font-medium text-accent hover:bg-surface-2"
          >
            Zobacz wszystkie powiadomienia →
          </Link>
        </div>
      )}
    </div>
  );
}

function NotificationItem({
  notification,
  isActionable,
  onMarkRead,
  onResolve,
  onClose,
}: {
  notification: Notification;
  isActionable: boolean;
  onMarkRead: () => void;
  onResolve: () => void;
  onClose: () => void;
}) {
  const handleClick = () => {
    // Klik decyduje per typ:
    // - Actionable (reminders, offer_*): NEW -> TODO (readAt only). Zostaje
    //   w bell counter dopoki user wykona akcje w leadzie (notatka/status/
    //   deadline -> backend auto-resolve) lub manual resolve w /notyfikacje.
    // - Info-only (lead_*, discount_*, feedback_*, system): klik = od razu
    //   RESOLVED. Brak stanu "do akcji" — nic do robienia.
    // Klient 2026-05-14: "rabaty, koszyki to nie maja zastosowania, chodzi
    // mi o przypomnienia kontaktow handlowca."
    if (!notification.resolvedAt) {
      if (isActionable) {
        if (!notification.readAt) onMarkRead();
      } else {
        onResolve();
      }
    }
    onClose();
  };

  const inner = (
    <div className="flex items-start justify-between gap-2 p-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {!notification.readAt && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />}
          <span className={cn('font-medium text-fg', !notification.readAt && 'text-fg')}>
            {notification.title}
          </span>
          {notification.eventCount > 1 && (
            <span
              className="rounded bg-accent-subtle px-1.5 py-0.5 text-xs font-semibold text-accent"
              title={`Zdarzenie powtórzyło się ${notification.eventCount} razy`}
            >
              {notification.eventCount}×
            </span>
          )}
        </div>
        {notification.body && (
          <div className="mt-0.5 truncate text-xs text-fg-muted">{notification.body}</div>
        )}
        <div className="mt-1 text-xs text-fg-subtle">{formatRelative(notification.createdAt)}</div>
      </div>
      {!notification.readAt && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMarkRead();
          }}
          className="shrink-0 rounded p-1 text-fg-muted hover:bg-surface-2 hover:text-fg"
          aria-label="Oznacz jako przeczytane"
          title="Oznacz jako przeczytane"
        >
          <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      )}
    </div>
  );

  return (
    <li className="border-b border-border last:border-b-0 transition-colors hover:bg-surface-2">
      {notification.url ? (
        <Link to={notification.url} onClick={handleClick}>
          {inner}
        </Link>
      ) : (
        <button type="button" onClick={handleClick} className="block w-full text-left">
          {inner}
        </button>
      )}
    </li>
  );
}

/** Kilka powiadomień jednego klienta w dzwonku: nazwisko, co się działo, kiedy. */
function ClientGroupItem({
  group,
  labelDot,
  onOpen,
  onResolveAll,
  onClose,
}: {
  group: NotificationGroup;
  labelDot?: NotificationLabelColor;
  onOpen: () => void;
  onResolveAll: () => void;
  onClose: () => void;
}) {
  const kinds = [...new Set(group.items.map((n) => notificationLabel(n.type)))];
  return (
    <li className="border-b border-border transition-colors last:border-b-0 hover:bg-surface-2">
      <Link
        to={`/crm/${group.leadId}`}
        onClick={() => {
          onOpen();
          onClose();
        }}
      >
        <div className="flex items-start justify-between gap-2 p-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {group.hasNew && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />}
              {labelDot && (
                <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', LABEL_DOT[labelDot])} />
              )}
              <span className="truncate font-medium text-fg">{group.leadName ?? 'Klient'}</span>
              <span className="shrink-0 rounded-md bg-surface-2 px-1.5 text-xs font-semibold tabular-nums text-fg-muted">
                {group.items.length}
              </span>
            </div>
            <div className="mt-0.5 truncate text-xs text-fg-muted">{kinds.join(' · ')}</div>
            <div className="mt-1 text-xs text-fg-subtle">{formatRelative(group.latestAt)}</div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onResolveAll();
            }}
            className="shrink-0 rounded p-1 text-fg-muted hover:bg-success-subtle hover:text-success"
            aria-label="Wszystko o tym kliencie załatwione"
            title="Wszystko załatwione — cała karta klienta"
          >
            <CheckCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        </div>
      </Link>
    </li>
  );
}
